import { Navigate, useLocation } from 'react-router-dom';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { SCOPES, type Scope } from '@homejournal/types';
import { useAuth } from '@/hooks/useAuth';
import { env } from '@/lib/env';
import { getSupabase, signOut } from '@/lib/supabase';
import { RouteFallback } from './RouteFallback';

type AllowState = 'unknown' | 'allowed' | 'denied';

const ALL_SCOPES: Scope[] = [...SCOPES];

interface ScopesCtx {
  scopes: Scope[];
  has: (s: Scope) => boolean;
}

const ScopesContext = createContext<ScopesCtx>({
  scopes: ALL_SCOPES,
  has: () => true,
});

export function useScopes(): ScopesCtx {
  return useContext(ScopesContext);
}

export function AuthGate({ children }: { children: ReactNode }) {
  const { status, session } = useAuth();
  const location = useLocation();
  const email = session?.user?.email ?? null;
  const [allow, setAllow] = useState<AllowState>('unknown');
  const [scopes, setScopes] = useState<Scope[]>(ALL_SCOPES);

  useEffect(() => {
    if (env.devBypassAuth) {
      setScopes(ALL_SCOPES);
      return;
    }
    if (status !== 'authenticated') {
      setAllow('unknown');
      setScopes([]);
      return;
    }
    let cancelled = false;
    (async () => {
      const supabase = getSupabase();
      const [allowRes, scopesRes] = await Promise.all([
        supabase.rpc('is_allowed'),
        supabase.rpc('allowed_scopes'),
      ]);
      if (cancelled) return;
      if (allowRes.error || allowRes.data !== true) {
        setAllow('denied');
        void signOut();
        return;
      }
      // If the scopes RPC hasn't been migrated yet, fail open on all scopes
      // so existing invited users don't get locked out.
      const next = normalizeScopes(scopesRes.data);
      setScopes(next.length > 0 ? next : ALL_SCOPES);
      setAllow('allowed');
    })();
    return () => {
      cancelled = true;
    };
  }, [status]);

  const ctx = useMemo<ScopesCtx>(
    () => ({ scopes, has: (s: Scope) => scopes.includes(s) }),
    [scopes],
  );

  if (env.devBypassAuth)
    return <ScopesContext.Provider value={ctx}>{children}</ScopesContext.Provider>;
  if (status === 'loading') return <RouteFallback />;
  if (status === 'unauthenticated') {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  if (allow === 'unknown') return <RouteFallback />;
  if (allow === 'denied') {
    return <Navigate to="/login" replace state={{ forbiddenEmail: email }} />;
  }
  return <ScopesContext.Provider value={ctx}>{children}</ScopesContext.Provider>;
}

function normalizeScopes(data: unknown): Scope[] {
  if (!Array.isArray(data)) return [];
  const valid = new Set<Scope>(SCOPES);
  return data.filter((s): s is Scope => typeof s === 'string' && valid.has(s as Scope));
}
